// src/pages/access/SignedInTab.tsx — Phase 9 c13 (Signed In tab).
//
// Read-only view sorted by last_login DESC. Groups by recency:
//   - Active in the last 24h
//   - Active in the last 7 days
//   - Active in the last 30 days
//   - Active before that
//   - Never signed in
//
// A "flat" toggle pill collapses the groups into one most-recent-first list.

import { useMemo, useState } from "react";
import { C, F } from "../../theme";
import { Avatar, Card, Section, Stamp } from "../../components/ui";
import type { AccessUser } from "./types";

interface SignedInTabProps {
  users: AccessUser[];
  onOpen: (userId: number) => void;
}

type ViewMode = "grouped" | "flat";

const HOUR_MS = 60 * 60 * 1000;
const DAY_MS = 24 * HOUR_MS;

interface Group {
  id: string;
  title: string;
  rows: AccessUser[];
}

function bucketUsers(users: AccessUser[], now: number): Group[] {
  const buckets: Record<string, AccessUser[]> = {
    "24h": [],
    "7d": [],
    "30d": [],
    "older": [],
    "never": [],
  };
  for (const u of users) {
    if (u.last_login === null) {
      buckets.never.push(u);
      continue;
    }
    const ts = Date.parse(u.last_login);
    if (Number.isNaN(ts)) {
      buckets.never.push(u);
      continue;
    }
    const ageMs = now - ts;
    if (ageMs < DAY_MS) buckets["24h"].push(u);
    else if (ageMs < 7 * DAY_MS) buckets["7d"].push(u);
    else if (ageMs < 30 * DAY_MS) buckets["30d"].push(u);
    else buckets.older.push(u);
  }
  const ordering: Array<[string, string]> = [
    ["24h", "Active in the last 24 hours"],
    ["7d", "Active in the last 7 days"],
    ["30d", "Active in the last 30 days"],
    ["older", "Active before that"],
    ["never", "Never signed in"],
  ];
  return ordering
    .map(([id, title]) => ({ id, title, rows: buckets[id] }))
    .filter((g) => g.rows.length > 0);
}

function relativeTime(iso: string | null, now: number): string {
  if (!iso) return "never";
  const ts = Date.parse(iso);
  if (Number.isNaN(ts)) return "—";
  const diff = now - ts;
  if (diff < 60_000) return "just now";
  if (diff < HOUR_MS) {
    const m = Math.floor(diff / 60_000);
    return `${m} min ago`;
  }
  if (diff < DAY_MS) {
    const h = Math.floor(diff / HOUR_MS);
    return `${h}h ago`;
  }
  if (diff < 2 * DAY_MS) return "yesterday";
  if (diff < 7 * DAY_MS) {
    const d = Math.floor(diff / DAY_MS);
    return `${d} days ago`;
  }
  if (diff < 30 * DAY_MS) {
    const w = Math.floor(diff / (7 * DAY_MS));
    return `${w} ${w === 1 ? "week" : "weeks"} ago`;
  }
  if (diff < 365 * DAY_MS) {
    const mo = Math.floor(diff / (30 * DAY_MS));
    return `${mo} ${mo === 1 ? "month" : "months"} ago`;
  }
  const y = Math.floor(diff / (365 * DAY_MS));
  return `${y} ${y === 1 ? "year" : "years"} ago`;
}

export function SignedInTab({ users, onOpen }: SignedInTabProps) {
  const [mode, setMode] = useState<ViewMode>("grouped");
  const now = useMemo(() => Date.now(), []);

  const sorted = useMemo(() => {
    return [...users].sort((a, b) => {
      const at = a.last_login ? Date.parse(a.last_login) : 0;
      const bt = b.last_login ? Date.parse(b.last_login) : 0;
      return bt - at;
    });
  }, [users]);

  const groups = useMemo(() => bucketUsers(sorted, now), [sorted, now]);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      <div style={{ display: "flex", gap: 6 }}>
        <ToggleChip on={mode === "grouped"} onClick={() => setMode("grouped")}>
          Grouped by recency
        </ToggleChip>
        <ToggleChip on={mode === "flat"} onClick={() => setMode("flat")}>
          Most recent first
        </ToggleChip>
      </div>

      {mode === "flat" ? (
        <Card padded={false}>
          {sorted.map((u, i) => (
            <SignedInRow
              key={u.id}
              user={u}
              isFirst={i === 0}
              relative={relativeTime(u.last_login, now)}
              onOpen={() => onOpen(u.id)}
            />
          ))}
        </Card>
      ) : (
        groups.map((g) => (
          <div key={g.id}>
            <Section title={g.title} sub={`${g.rows.length} ${g.rows.length === 1 ? "user" : "users"}`} />
            <Card padded={false}>
              {g.rows.map((u, i) => (
                <SignedInRow
                  key={u.id}
                  user={u}
                  isFirst={i === 0}
                  relative={relativeTime(u.last_login, now)}
                  onOpen={() => onOpen(u.id)}
                />
              ))}
            </Card>
          </div>
        ))
      )}
    </div>
  );
}

function SignedInRow({
  user,
  isFirst,
  relative,
  onOpen,
}: {
  user: AccessUser;
  isFirst: boolean;
  relative: string;
  onOpen: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onOpen}
      style={{
        display: "grid",
        gridTemplateColumns: "44px 1fr auto",
        gap: 12,
        padding: "12px 16px",
        width: "100%",
        alignItems: "center",
        background: "transparent",
        border: "none",
        borderTop: isFirst ? "none" : `1px solid ${C.hairlineLt}`,
        cursor: "pointer",
        textAlign: "left",
      }}
    >
      <Avatar first={user.first_name} last={user.last_name} size={44} tone="brass" />
      <div style={{ minWidth: 0 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
          <span
            style={{
              fontFamily: F.serif,
              fontSize: 16,
              fontWeight: 700,
              color: C.ink,
              letterSpacing: -0.2,
            }}
          >
            {user.first_name} {user.last_name}
          </span>
          {user.is_blocked && <Stamp tone="signal">Blocked</Stamp>}
          {user.sign_on_status === "pending" && <Stamp tone="brass">Pending</Stamp>}
        </div>
        <div
          style={{
            fontFamily: F.body,
            fontSize: 12,
            color: C.inkSoft,
            fontWeight: 600,
            marginTop: 2,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {user.email}
        </div>
        {user.departments.length > 0 && (
          <div
            style={{
              fontFamily: F.body,
              fontSize: 11,
              color: C.inkMute,
              fontWeight: 500,
              marginTop: 3,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {user.departments.join(", ")}
          </div>
        )}
      </div>
      <div
        style={{
          fontFamily: F.mono,
          fontSize: 11,
          color: C.inkMute,
          fontWeight: 700,
          letterSpacing: 0.3,
          textAlign: "right",
          whiteSpace: "nowrap",
        }}
      >
        {relative}
      </div>
    </button>
  );
}

function ToggleChip({
  on,
  onClick,
  children,
}: {
  on: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        padding: "7px 13px",
        borderRadius: 999,
        border: on ? `1.5px solid ${C.brassDk}` : `1px solid ${C.hairline}`,
        background: on ? C.brassBg : C.card,
        color: on ? C.brassDk : C.inkSoft,
        fontFamily: F.body,
        fontSize: 13,
        fontWeight: 700,
        letterSpacing: 0.2,
        cursor: "pointer",
      }}
    >
      {children}
    </button>
  );
}
